<script setup lang="ts">
  defineOptions({ name: 'EventDetailAboutSection' })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Globe01Icon, Mail01Icon, PhoneIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPText from '@ui/components/CPText.vue'

  const props = defineProps({
    title: {
      type: String,
      required: true,
    },
    about: {
      type: String,
      default: '',
    },
    organizerName: {
      type: String,
      default: '',
    },
    organizerEmail: {
      type: String,
      default: '',
    },
    organizerPhone: {
      type: String,
      default: '',
    },
    organizerWebsite: {
      type: String,
      default: '',
    },
    organizerImageUrl: {
      type: String,
      default: '',
    },
    dateLabel: {
      type: String,
      default: '',
    },
    timeLabel: {
      type: String,
      default: '',
    },
    locationLabel: {
      type: String,
      default: '',
    },
    formatKind: {
      type: String as PropType<'online' | 'in-person'>,
      default: 'in-person',
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const { t } = useI18n()

  const formatLabel = computed(() =>
    props.formatKind === 'online' ? t('clientportal.events.detail.formatOnline') : t('clientportal.events.detail.formatInPerson')
  )

  const metaRows = computed(() =>
    [
      { id: 'dates', label: t('clientportal.events.detail.metaDates'), value: props.dateLabel.trim() },
      { id: 'time', label: t('clientportal.events.detail.metaTime'), value: props.timeLabel.trim() },
      { id: 'location', label: t('clientportal.events.detail.metaLocation'), value: props.locationLabel.trim() },
      { id: 'format', label: t('clientportal.events.detail.metaFormat'), value: formatLabel.value },
    ].filter(row => row.value)
  )

  const organizerEmail = computed(() => props.organizerEmail.trim())
  const organizerPhone = computed(() => props.organizerPhone.trim())
  const organizerWebsite = computed(() => props.organizerWebsite.trim())
  const organizerPhoneHref = computed(() => {
    const compact = organizerPhone.value.replace(/[^\d+]/g, '')
    return compact ? `tel:${compact}` : ''
  })
  const organizerWebsiteLabel = computed(() => {
    const url = organizerWebsite.value
    if (!url) return ''
    try {
      return new URL(url).hostname.replace(/^www\./, '')
    } catch (error) {
      console.error('[EventDetailAboutSection] Invalid organizer website:', error)
      return t('clientportal.events.detail.organizerWebsite')
    }
  })
  const hasOrganizer = computed(() => Boolean(props.organizerName.trim()))
  const hasOrganizerContacts = computed(
    () => Boolean(organizerEmail.value) || Boolean(organizerPhoneHref.value) || Boolean(organizerWebsite.value)
  )
</script>

<template>
  <section
    :id="createElementId(idPrefix, 'section', 'about')"
    class="flex flex-col gap-4 rounded-xl border border-solid border-[var(--cp-border-200)] bg-background p-6 shadow-sm"
  >
    <CPText
      :id="createElementId(idPrefix, 'about', 'title')"
      type="display"
      size="sm"
      weight="bold"
      html-tag="h1"
      class="text-pretty tracking-[-0.02em] text-body"
    >
      {{ title }}
    </CPText>

    <div
      v-if="metaRows.length > 0"
      :id="createElementId(idPrefix, 'about', 'meta')"
      class="grid grid-cols-2 gap-x-6 gap-y-4 border-y border-solid border-[var(--cp-border-200)] py-5 sm:grid-cols-4 lg:gap-x-8"
    >
      <div v-for="row in metaRows" :id="createElementId(idPrefix, 'about-meta', row.id)" :key="row.id" class="flex min-w-0 flex-col gap-1">
        <div class="hidden lg:block">
          <CPText size="xl" weight="normal" class="text-500">{{ row.label }}</CPText>
          <CPText size="2xl" weight="semibold" class="text-pretty text-body">{{ row.value }}</CPText>
        </div>
        <div class="block lg:hidden">
          <CPText size="md" weight="normal" class="text-500">{{ row.label }}</CPText>
          <CPText size="lg" weight="semibold" class="text-pretty text-body">{{ row.value }}</CPText>
        </div>
      </div>
    </div>

    <CPText v-if="about" :id="createElementId(idPrefix, 'about', 'description')" size="md" weight="normal" class="text-pretty text-600">
      {{ about }}
    </CPText>

    <div
      v-if="hasOrganizer || hasOrganizerContacts"
      :id="createElementId(idPrefix, 'organizer', 'footer')"
      class="mt-1 flex flex-wrap items-center gap-3.5 border-t border-solid border-[var(--cp-border-200)] pt-4"
    >
      <CPAvatar
        :title="organizerName"
        :src="organizerImageUrl"
        :size="48"
        shape="circle"
        source="client-portal"
        :use-raw-url="true"
        class="shrink-0"
      />
      <div class="flex min-w-0 flex-col gap-0.5">
        <CPText size="xs" weight="semibold" class="uppercase tracking-[0.1em] text-400">
          {{ t('clientportal.events.detail.organizedByLabel') }}
        </CPText>
        <CPText :id="createElementId(idPrefix, 'organizer', 'name')" size="md" weight="semibold" class="text-body">
          {{ organizerName }}
        </CPText>
      </div>
      <div
        v-if="hasOrganizerContacts"
        :id="createElementId(idPrefix, 'organizer', 'contacts')"
        class="ml-auto flex flex-wrap items-center gap-2"
      >
        <CPButton
          v-if="organizerEmail"
          :id="createElementId(idPrefix, 'organizer', 'email')"
          type="secondary"
          color="secondary"
          size="sm"
          :href="`mailto:${organizerEmail}`"
          :aria-label="t('clientportal.events.detail.organizerEmail')"
        >
          <template #iconLeft>
            <Mail01Icon class="h-4 w-4" aria-hidden="true" />
          </template>
          {{ t('clientportal.events.detail.organizerEmail') }}
        </CPButton>
        <CPButton
          v-if="organizerWebsite"
          :id="createElementId(idPrefix, 'organizer', 'website')"
          type="secondary"
          color="secondary"
          size="sm"
          :href="organizerWebsite"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="t('clientportal.events.detail.organizerWebsite')"
        >
          <template #iconLeft>
            <Globe01Icon class="h-4 w-4" aria-hidden="true" />
          </template>
          {{ organizerWebsiteLabel }}
        </CPButton>
        <CPButton
          v-if="organizerPhone && organizerPhoneHref"
          :id="createElementId(idPrefix, 'organizer', 'phone')"
          type="secondary"
          color="secondary"
          size="sm"
          :href="organizerPhoneHref"
          :aria-label="t('clientportal.events.detail.organizerPhone')"
        >
          <template #iconLeft>
            <PhoneIcon class="h-4 w-4" aria-hidden="true" />
          </template>
          {{ t('clientportal.events.detail.organizerPhone') }}
        </CPButton>
      </div>
    </div>
  </section>
</template>
